import { useEffect, useMemo, useRef } from 'react';
import { useGrpcClient } from './useGrpcClient';
import { useStatus } from './useStatus';
import { useProjects } from './useProjects';
import { useArtifacts } from './useArtifacts';
import { useBasemap } from './useBasemap';
import { projectService } from '../services/projectService';
import { useUserProjectsContext } from '../contexts/UserProjectsContext';
import { useEventSubscribe } from '../events';
// Performance monitoring in development
const useRenderCount = (componentName) => {
    if (process.env.NODE_ENV === 'development') {
    }
};
export const useGrpcService = (viewer) => {
    // Performance monitoring
    useRenderCount('useGrpcService');
    // Initialize all the individual hooks
    const grpcClient = useGrpcClient();
    const status = useStatus();
    // Initialize projects hook first (without artifacts initially)
    const projects = useProjects(viewer, new Map(), // Start with empty artifacts map
    () => { }, // Empty clear function initially
    status.addStatus);
    // Initialize artifacts hook with the selected project from projects
    const artifacts = useArtifacts(viewer, projects.selectedProject, status.addStatus);
    // Initialize basemap hook with artifacts
    const basemap = useBasemap(viewer, artifacts.artifactState.loaded, artifacts.artifactActions.updateLoaded, status.addStatus);
    // Update the projects hook's clearAllArtifacts function to use the actual artifacts clear function
    // This is done through the return object
    const clearAllArtifactsFunction = artifacts.artifactActions.clearAll;
    // Combine all hook returns with memoization for performance
    // Track user project IDs for polling refresh logic
    const userProjectIdSetRef = useRef(new Set());
    const { projects: userProjects } = useUserProjectsContext();
    // Sync user project IDs into ref for routing logic (user vs demo service)
    useEffect(() => {
        const idSet = new Set(userProjects.map(p => p.id));
        userProjectIdSetRef.current = idSet;
    }, [userProjects]);
    // Handle requests to refresh project structure for the currently selected project
    useEventSubscribe('data:refreshProjectStructure', (payload) => {
        try {
            const requestedProjectId = payload.projectId;
            const current = projects.selectedProject;
            if (!current)
                return;
            const selectedId = current.getId ? current.getId() : undefined;
            if (!requestedProjectId || requestedProjectId !== selectedId) {
                return;
            }
            // If this is a user project, refresh via user projects service; otherwise use the demo projects service
            if (selectedId && userProjectIdSetRef.current.has(selectedId)) {
                // Always use authenticated user projects service for user projects
                projectService
                    .getUserProjectStructure(selectedId)
                    .then(({ folders, artifacts, layerStatuses }) => {
                    if (projects.setFolders)
                        projects.setFolders(folders);
                    if (projects.setArtifacts)
                        projects.setArtifacts(artifacts);
                    if (projects.buildFolderTree && projects.setFolderTree) {
                        // Pass layerStatuses so still-processing/failed layers keep their
                        // placeholder folders across polls. Without this the poll rebuilds
                        // the tree without statuses and prunes the placeholder, making it
                        // flicker in and out against the initial load.
                        const tree = projects.buildFolderTree(folders, artifacts, layerStatuses);
                        projects.setFolderTree(tree);
                    }
                    // Removed autoload notification: updating structure should not auto-load artifacts
                })
                    .catch(() => {
                    // swallow errors; UI polling continues
                });
            }
            else {
                // Ask backend for latest structure via existing API for demo projects
                // Use silent refresh for demos as well to avoid loader flicker when unchanged
                if (projects.refreshProjectStructureSilently) {
                    projects.refreshProjectStructureSilently(selectedId);
                }
                else {
                    projects.loadProjectStructure(selectedId);
                }
            }
        }
        catch (_e) { }
    }, [projects]);
    return useMemo(() => ({
        // Projects
        ...projects,
        clearAllArtifacts: clearAllArtifactsFunction, // Override with actual artifacts clear function
        // Artifacts
        ...artifacts,
        loadedArtifacts: artifacts.artifactState.loaded,
        artifactControls: artifacts.artifactState.controls,
        activeInfoPanels: artifacts.artifactState.activeOverlays.info,
        activeScreenOverlays: artifacts.artifactState.activeOverlays.screen,
        setLoadedArtifacts: artifacts.artifactActions.updateLoaded, // For compatibility
        // Basemap
        ...basemap,
        // Status
        ...status,
        // Client status
        clientConnected: grpcClient.isConnected
    }), [
        projects,
        artifacts,
        basemap,
        status,
        grpcClient.isConnected,
        clearAllArtifactsFunction
    ]);
};
